import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Inspection, Verdict } from '@visionqc/shared';
import { api } from '../api/client';
import { useProducts } from '../api/useProducts';
import { useImageUrl } from '../api/useImageUrl';
import { ImageWithHeatmap } from '../components/ImageWithHeatmap';
import { VerdictBadge } from '../components/common';
import { fmtTime } from '../lib/format';

function Thumb({ imageKey }: { imageKey: string }) {
  const url = useImageUrl(imageKey);
  return url ? <img className="thumb" src={url} alt="" /> : <div className="thumb" />;
}

export function HistoryView() {
  const { products } = useProducts();
  const [productId, setProductId] = useState<number | undefined>();
  const [verdict, setVerdict] = useState<Verdict | ''>('');
  const [items, setItems] = useState<Inspection[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Inspection | null>(null);
  const navigate = useNavigate();

  const load = useCallback(
    async (reset: boolean) => {
      setLoading(true);
      try {
        const res = await api.listInspections({
          productId,
          verdict: verdict || undefined,
          cursor: reset ? undefined : (cursor ?? undefined),
          limit: 25,
        });
        setItems((prev) => (reset ? res.items : [...prev, ...res.items]));
        setCursor(res.nextCursor);
      } finally {
        setLoading(false);
      }
    },
    [productId, verdict, cursor],
  );

  // Reload from scratch when filters change.
  useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, verdict]);

  return (
    <div>
      <h1 className="view-title">Inspection history</h1>

      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="row">
          <div>
            <label>Product</label>
            <select
              value={productId ?? ''}
              onChange={(e) => setProductId(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">All products</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Verdict</label>
            <select value={verdict} onChange={(e) => setVerdict(e.target.value as Verdict | '')}>
              <option value="">All</option>
              <option value="GOOD">GOOD</option>
              <option value="BAD">BAD</option>
              <option value="UNSURE">UNSURE</option>
            </select>
          </div>
        </div>
      </div>

      <div className="panel">
        <table>
          <thead>
            <tr>
              <th></th>
              <th>Verdict</th>
              <th>Score</th>
              <th>Latency</th>
              <th>Model</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} onClick={() => setSelected(i)}>
                <td>
                  <Thumb imageKey={i.imageKey} />
                </td>
                <td>
                  <VerdictBadge verdict={i.verdict} />
                </td>
                <td style={{ fontFamily: 'var(--mono)' }}>{i.score.toFixed(3)}</td>
                <td>{i.latencyMs} ms</td>
                <td className="muted">{i.modelVersion}</td>
                <td className="muted">{fmtTime(i.time)}</td>
              </tr>
            ))}
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 40 }}>
                  No inspections yet. Run one from the Live view.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="row" style={{ marginTop: 16, justifyContent: 'center' }}>
          {cursor && (
            <button className="btn" onClick={() => load(false)} disabled={loading}>
              {loading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </div>

      {selected && (
        <DetailPanel
          inspection={selected}
          onClose={() => setSelected(null)}
          onCorrect={() =>
            navigate('/annotate', {
              state: {
                imageKey: selected.imageKey,
                inspectionId: selected.id,
                productId: selected.productId,
                suggestedLabel: selected.verdict,
              },
            })
          }
        />
      )}
    </div>
  );
}

function DetailPanel({
  inspection,
  onClose,
  onCorrect,
}: {
  inspection: Inspection;
  onClose: () => void;
  onCorrect: () => void;
}) {
  const [showHeatmap, setShowHeatmap] = useState(true);
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        className="panel"
        style={{ maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>
            <VerdictBadge verdict={inspection.verdict} />{' '}
            <span className="muted" style={{ fontSize: 16 }}>
              score {inspection.score.toFixed(3)}
            </span>
          </h2>
          <button className="btn sm" onClick={onClose}>
            ✕ Close
          </button>
        </div>

        <label className="row" style={{ gap: 8, marginBottom: 12 }}>
          <input type="checkbox" checked={showHeatmap} onChange={(e) => setShowHeatmap(e.target.checked)} />
          Show heatmap overlay
        </label>

        <ImageWithHeatmap
          imageKey={inspection.imageKey}
          heatmapKey={inspection.heatmapKey}
          showHeatmap={showHeatmap}
        />

        <div className="muted" style={{ marginTop: 12, fontFamily: 'var(--mono)', fontSize: 14 }}>
          {inspection.latencyMs} ms · {inspection.modelVersion} · {fmtTime(inspection.time)}
        </div>

        <button className="btn primary" style={{ marginTop: 16 }} onClick={onCorrect}>
          ✎ Correct verdict / annotate
        </button>
      </div>
    </div>
  );
}
